import { useCallback, useEffect, useMemo, useRef, type ReactNode } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import type { PolicyCommitmentRailRow } from '@deepguide-ai/types'
import { Button } from '@deepguide-ai/ui/button'
import { ScrollArea } from '@deepguide-ai/ui/scroll-area'
import { AlertTriangle, Loader2, X } from 'lucide-react'
import { trpc } from '@/lib/trpc'
import { TriageClaimCard } from './TriageClaimCard'
import type { TriageClaim } from './types'

type PolicyCommitmentsPanelProps = {
  workspaceId: string
  auditCycleId?: string
  programId: string
  policySlug: string
  policyDocumentId?: string
  version?: number
  editable: boolean
  canManageTestLinks?: boolean
  missingCoverageOnly?: boolean
  onClearMissingCoverageFilter?: () => void
  /** Claim clicked in the document — the matching claim lights up. */
  selectedClaimId?: string | null
  /** Claim clicked here — the matching sentence lights up in the document. */
  onSelectClaim?: (claimId: string) => void
  onPolicyQueued?: () => void
  headerAction?: ReactNode
}

/**
 * The policy's Claims tab: every claim in document order, each with the
 * sentence it quotes from the policy and the tests that verify it. The
 * questionnaire answers behind the claims live in the questionnaire and the
 * policy's review dialog, not here (2026-09-30).
 */
export function PolicyCommitmentsPanel({
  workspaceId,
  auditCycleId,
  programId,
  policySlug,
  policyDocumentId,
  version,
  editable,
  canManageTestLinks = editable,
  missingCoverageOnly = false,
  onClearMissingCoverageFilter,
  selectedClaimId,
  onSelectClaim,
  onPolicyQueued,
  headerAction,
}: PolicyCommitmentsPanelProps) {
  const queryClient = useQueryClient()
  const railQuery = useQuery(
    trpc.complianceProgram.getPolicyCommitments.queryOptions(
      {
        programId,
        policySlug,
      },
      {
        refetchInterval: (query) => {
          const projection = query.state.data?.projection
          return projection &&
            projection.requestedGeneration > projection.appliedGeneration
            ? projection.status === 'FAILED'
              ? 5_000
              : 1_000
            : false
        },
      }
    )
  )
  const claimsQuery = useQuery(
    trpc.claimTriage.list.queryOptions({
      programId, policyName: policySlug,
      ...(version !== undefined ? { policyDocumentId, version } : {}),
    })
  )
  const refreshClaimCoverage = useCallback(() => {
    void Promise.all([
      queryClient.invalidateQueries({
        queryKey: trpc.claimTriage.list.queryKey({
          programId,
          policyName: policySlug,
        }),
      }),
      queryClient.invalidateQueries({ queryKey: trpc.complianceProgram.getPolicyHealth.queryKey() }),
      queryClient.invalidateQueries({ queryKey: trpc.complianceProgram.getPolicyCommitments.queryKey() }),
      queryClient.invalidateQueries({ queryKey: trpc.lineage.getClaimChain.queryKey() }),
    ])
  }, [policySlug, programId, queryClient])
  const projectionPending = Boolean(
    railQuery.data?.projection &&
    railQuery.data.projection.requestedGeneration >
      railQuery.data.projection.appliedGeneration
  )
  const wasProjectionPending = useRef(false)

  /** Every claim, in the order its sentence appears in the policy, with the
   *  sentence as written. Claims the rail doesn't place follow at the end. */
  const orderedClaims = useMemo(() => {
    const claims = (claimsQuery.data ?? []) as TriageClaim[]
    const claimsById = new Map(claims.map((claim) => [claim.id, claim]))
    const ordered: Array<{ claim: TriageClaim; quote: string | null }> = []
    const placed = new Set<string>()
    for (const row of (railQuery.data?.rows ?? []) as PolicyCommitmentRailRow[]) {
      for (const projection of row.projections) {
        if (!projection.active || placed.has(projection.commitmentId)) continue
        const claim = claimsById.get(projection.commitmentId)
        if (!claim) continue
        placed.add(claim.id)
        ordered.push({ claim, quote: claim.quoteText })
      }
    }
    for (const claim of claims) {
      if (!placed.has(claim.id)) ordered.push({ claim, quote: claim.quoteText })
    }
    return ordered
  }, [claimsQuery.data, railQuery.data?.rows])
  const visibleClaims = missingCoverageOnly
    ? orderedClaims.filter(({ claim }) => claim.claimTestLinks.length === 0)
    : orderedClaims
  const coverageFilterResolved =
    missingCoverageOnly &&
    railQuery.isSuccess &&
    claimsQuery.isSuccess &&
    visibleClaims.length === 0

  useEffect(() => {
    if (coverageFilterResolved) onClearMissingCoverageFilter?.()
  }, [coverageFilterResolved, onClearMissingCoverageFilter])

  useEffect(() => {
    if (wasProjectionPending.current && !projectionPending) {
      // The projection rewrote the document in the worker — refresh the
      // document body, version history and claims, or the open editor keeps
      // showing pre-regeneration content.
      queryClient.invalidateQueries({
        queryKey: trpc.complianceProgram.getPolicyDocContent.queryKey(),
      })
      queryClient.invalidateQueries({
        queryKey: trpc.complianceProgram.getPolicyVersions.queryKey(),
      })
      queryClient.invalidateQueries({
        queryKey: trpc.complianceProgram.getPolicies.queryKey(),
      })
      refreshClaimCoverage()
      onPolicyQueued?.()
    }
    wasProjectionPending.current = projectionPending
  }, [onPolicyQueued, projectionPending, queryClient, refreshClaimCoverage])

  if (railQuery.isLoading || claimsQuery.isLoading) {
    return (
      <div className='text-muted-foreground flex items-center gap-2 p-4 text-xs'>
        <Loader2 className='size-3.5 animate-spin' />
        Loading claims…
      </div>
    )
  }
  if (orderedClaims.length === 0) {
    return (
      <div className='flex items-center justify-between gap-2 p-4'>
        <span className='text-muted-foreground text-xs'>
          No claims in this policy yet.
        </span>
        {headerAction}
      </div>
    )
  }
  if (coverageFilterResolved && onClearMissingCoverageFilter) return null

  return (
    <ScrollArea className='min-h-0 flex-1' viewClassName='[&>div]:!block p-3'>
      <div className='space-y-3'>
        {headerAction && <div className='flex justify-end'>{headerAction}</div>}
        {missingCoverageOnly && (
          <div className='bg-card flex items-center justify-between gap-2 rounded-md border px-3 py-2.5'>
            <span className='text-muted-foreground text-xs'>
              Showing claims without linked tests
            </span>
            <Button
              variant='ghost'
              size='sm'
              onClick={onClearMissingCoverageFilter}
            >
              <X />
              Clear
            </Button>
          </div>
        )}
        {projectionPending && (
          <div className='bg-card text-muted-foreground flex items-start gap-2 rounded-md border px-3 py-2.5 text-xs'>
            {railQuery.data?.projection?.status === 'FAILED' ? (
              <AlertTriangle className='text-status-orange mt-0.5 size-3.5 shrink-0' />
            ) : (
              <Loader2 className='mt-0.5 size-3.5 shrink-0 animate-spin' />
            )}
            <span>
              {railQuery.data?.projection?.status === 'FAILED'
                ? 'Policy update will retry automatically.'
                : 'Updating policy wording from the latest answer…'}
            </span>
          </div>
        )}
        {visibleClaims.map(({ claim, quote }) => (
          <div key={claim.id} data-claim-id={claim.id}>
            <TriageClaimCard
              claim={claim}
              quote={quote}
              workspaceId={workspaceId}
              auditCycleId={auditCycleId}
              readonly={!editable}
              canManageTestLinks={canManageTestLinks}
              isSelected={claim.id === selectedClaimId}
              onClick={() => onSelectClaim?.(claim.id)}
              onResolved={refreshClaimCoverage}
            />
          </div>
        ))}
      </div>
    </ScrollArea>
  )
}
